/**
 * Which deployment environment is this code running in?
 *
 * Mucka runs three environments that share one codebase but point at
 * different Supabase projects (see docs/environments-plan.md):
 *
 *   - `prod` — app.mucka.ai, the real customer database
 *   - `dev`  — dev.mucka.ai + every Vercel preview, a throwaway database
 *   - `demo` — demo.mucka.ai, the sales demo with self-refreshing data
 *
 * The environment is carried in a single env var, `NEXT_PUBLIC_APP_ENV`,
 * scoped per-deployment in Vercel (Production / Preview). It is `NEXT_PUBLIC_`
 * so the same answer is available on the client (env banner) and the server
 * (the outbound-comms guard).
 *
 * SAFETY DEFAULT: anything other than an explicit `dev` / `demo` resolves to
 * `prod`. A missing or mistyped flag must NEVER silence real customer comms,
 * so the failure mode of "forgot to set the var" is "behaves like production",
 * not "production goes silent". Non-prod deployments must therefore set
 * NEXT_PUBLIC_APP_ENV=dev|demo explicitly to opt into the sandbox.
 */

export type AppEnv = 'dev' | 'demo' | 'prod';

export function getAppEnv(): AppEnv {
  const raw = (process.env.NEXT_PUBLIC_APP_ENV || '').trim().toLowerCase();
  if (raw === 'dev' || raw === 'development') return 'dev';
  if (raw === 'demo') return 'demo';
  // Unset, blank, or unrecognised → production (see SAFETY DEFAULT above).
  return 'prod';
}

export const isProdEnv = (): boolean => getAppEnv() === 'prod';
export const isDevEnv = (): boolean => getAppEnv() === 'dev';
export const isDemoEnv = (): boolean => getAppEnv() === 'demo';

/** True for any non-production deployment (dev or demo). */
export const isNonProdEnv = (): boolean => getAppEnv() !== 'prod';
